import React, { PureComponent, ReactNode } from 'react';
import { Button, Divider, Input, notification, Select, Space } from 'antd';
import { Flex } from '../../common/Flex';
import { ModalPanelProps } from '../DashboardModal';
import {
    CountType,
    TimePeriod,
    TrafficVolume,
    TrafficVolumeListRequest,
    TrafficVolumeListRequest_IdType,
} from 'app/model/CORE';
import { ServiceFactory } from 'app/service/ServiceFactory';
import KeyUtils from '../../../common/util/KeyUtils';
import TrafficVolumeTable, {
    StandardTrafficVolumeColumn,
    TrafficVolumeColumn,
    TV_SELECTED_COLUMN,
} from '../../table/TrafficVolumeTable';
import memoizeOne from 'memoize-one';
import { MULTIPLE_SELECTION } from 'app/common/table/ManagedTable';
import { SelectionLabel } from '../../common/SelectionLabel';
import Utils from '../../../common/util/Utils';
import { MonitorAdditionalTrafficVolumeRequest } from 'app/model/SOL49';
import ExtensiblePanel, { ExtensiblePanelProps, ExtensiblePanelState } from '../../../common/panel/ExtensiblePanel';
import './QueryPanel.less';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { MenuUtils } from 'app/components/dashboard/common/MenuUtils';
import AppContext from 'app/common/context/AppContext';
import { MapSelectionValues, NewLayerPopup } from 'app/components/dashboard/network/map/MapSelectionPopup';
import { MapLayerUtils, MapObjectType } from 'app/components/dashboard/network/map/Map';
import { NewCustomLayerEvent, ShowModalEvent } from 'app/common/eventbus/Events';
import { RootState } from '../../../redux/store';
import {
    addToFlowSection,
    createFlowSection,
    selectUserCustomFlows,
} from '../../../redux/features/dashboard/user-monitored-flows-slice';
import { connect, ConnectedProps } from 'react-redux';

enum QueryType {
    TRAFFIC_VOLUME = 'Traffic Volume',
}

const TV_COLUMNS: TrafficVolumeColumn[] = [
    TV_SELECTED_COLUMN,
    StandardTrafficVolumeColumn.ID,
    StandardTrafficVolumeColumn.REF_LOC,
    StandardTrafficVolumeColumn.MONITORABLE,
];

const mapStateToProps = (state: RootState) => {
    return {
        customFlowSections: selectUserCustomFlows(state),
    };
};
const mapMapDispatchToProps = {
    createFlowSection,
    addToFlowSection,
};

const connector = connect(mapStateToProps, mapMapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface QueryPanelProps extends ModalPanelProps, ExtensiblePanelProps, PropsFromRedux {
    initialQuery?: string;
}

interface QueryPanelState extends ExtensiblePanelState {
    queryType: QueryType;
    tvQuery: string;
    tvQueryResult: Array<TrafficVolume>;
    selectedTvs: Array<TrafficVolume>;

    selectedPeriod: TimePeriod;
    selectedPeriodType: CountType;
}

class QueryPanelComponent extends ExtensiblePanel<QueryPanelProps, QueryPanelState> {
    static MAIN_PANEL_WIDTH_EM = 30;
    static SIDE_PANEL_WIDTH_EM = 40;
    public static INITIAL_WIDTH = QueryPanelComponent.MAIN_PANEL_WIDTH_EM + 'em';

    constructor(props) {
        super(props);
        this.state = Object.assign(this.state, {
            queryType: QueryType.TRAFFIC_VOLUME,
            tvQuery: this.props.initialQuery || '',
            tvQueryResult: [],
            selectedTvs: [],
            selectedPeriod: undefined,
            selectedPeriodType: undefined,
        });
    }

    componentDidMount() {
        if (Utils.isNotEmptyString(this.state.tvQuery)) {
            this.handleLaunchQuery();
        }
    }

    handleQueryTypeChange = (queryType: QueryType) => {
        this.setState({ queryType: queryType });
    };
    handleTvIdChange = e => {
        this.setState({ tvQuery: Utils.toUpperCase(e.currentTarget?.value) });
    };
    handleLaunchQuery = () => {
        const request = {
            dataset: AppContext.getDataset(),
            idType: TrafficVolumeListRequest_IdType.TV,
            //refLocTypes? : Array<RefLocationType>,
            wildcardIds: [this.state.tvQuery],
        } as TrafficVolumeListRequest;
        ServiceFactory.coreService()
            .getTrafficVolumeList(request)
            .then(reply => {
                this.setState(
                    {
                        tvQueryResult: reply.data,
                    },
                    () => {
                        this.showSidePanel();
                    },
                );
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            });
    };

    handleSelectionChange = (selection: Array<TrafficVolume>) => {
        this.setState({ selectedTvs: selection });
    };
    handleSelectAll = (selection: Array<TrafficVolume>) => {
        const newSelection = Utils.copyAndAddAllIfNeeded(this.state.selectedTvs, selection);
        return () => this.handleSelectionChange(newSelection);
    };
    handleClearSelection = () => {
        this.handleSelectionChange([]);
    };
    // No need to memoize this selection, it is being used only in the side panel content,
    // which is already memoized with same arguments (queryResult & selectedTvs)
    selection = () => ({
        selectionMode: MULTIPLE_SELECTION,
        onSelectionChange: this.handleSelectionChange,
        selectedItems: this.state.selectedTvs,
    });

    monitorExtraTvIds = (additionalTVs: Array<string>) => {
        const request = {
            additionalTVs: additionalTVs,
        } as MonitorAdditionalTrafficVolumeRequest;
        ServiceFactory.sol49Service()
            .monitorAdditionalTrafficVolume(request)
            .then(reply => {
                console.log(reply);
            });
    };

    getSelectionLabelMenuItems = (): ItemType[] => {
        const selectedTvIds = this.state.selectedTvs.map(tv => tv.id);
        return [
            { key: 'divider-1', type: 'divider' },
            MenuUtils.addToCustomSectionMenu(
                this.props.customFlowSections,
                payload => this.props.addToFlowSection(payload),
                payload => this.props.createFlowSection(payload),
                selectedTvIds,
                'queryPanel',
                this.monitorExtraTvIds,
            ),
            { key: 'divider-2', type: 'divider' },
            MenuUtils.addToMapWorkspaceLayerMenu(selectedTvIds, 'queryPanel'),
            MenuUtils.addToCustomLayer(selectedTvIds),
            {
                label: 'Create a custom layer',
                key: 'create-custom-layer',
                onClick: () => this.showCustomLayerNamePopup(selectedTvIds),
            },
        ];
    };

    handleSaveNewLayer = (value: MapSelectionValues, name: string) => {
        const values: Map<MapObjectType, Array<string>> = new Map<MapObjectType, Array<string>>();
        values.set(MapObjectType.TV, value[MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD].selected);
        NewCustomLayerEvent.fire(name, values);
    };

    showCustomLayerNamePopup(tvs: string[]) {
        const value: MapSelectionValues = Object.assign(
            {},
            {
                [MapLayerUtils.TV_MAP_SELECTION_POPUP_FIELD]: {
                    label: 'TFV',
                    options: tvs,
                    selected: tvs,
                },
            },
        );
        const content = <NewLayerPopup onSave={name => this.handleSaveNewLayer(value, name)} />;
        AppContext.eventBus.publish(new ShowModalEvent(ShowModalEvent.NAME, content, '200px'));
    }

    getPanels() {
        const panels = new Map<ReactNode, number>();
        panels.set(this.getMainPanelContent(), QueryPanel.MAIN_PANEL_WIDTH_EM);
        panels.set(
            this.getSidePanelContent(this.state.tvQueryResult, this.state.selectedTvs),
            QueryPanel.SIDE_PANEL_WIDTH_EM,
        );
        return panels;
    }

    getMainPanelContent = () => {
        return (
            <Flex className="query-panel" direction="vertical">
                <Flex className="modal-title" grow={false} shrink={false}>
                    <Flex direction="vertical">
                        <Flex className="title">Query</Flex>
                    </Flex>
                </Flex>
                <Flex grow={false} shrink={false}>
                    <Select value={this.state.queryType} onChange={this.handleQueryTypeChange}>
                        <Select.Option value={QueryType.TRAFFIC_VOLUME}>Traffic Volume</Select.Option>
                    </Select>
                </Flex>
                <Divider type="horizontal" />
                <Flex direction="vertical">
                    Traffic Volume
                    <div className="query">
                        <Input
                            value={this.state.tvQuery}
                            onChange={this.handleTvIdChange}
                            onKeyPress={e => e.key === 'Enter' && this.handleLaunchQuery()}
                        />
                    </div>
                </Flex>
                <Divider type="horizontal" />
                <Flex className="buttons" grow={false} shrink={false}>
                    <QueryPanelButtons onClose={this.props.onClose} onLaunchQuery={this.handleLaunchQuery} />
                </Flex>
            </Flex>
        );
    };

    getSidePanelContent = memoizeOne((tvs: TrafficVolume[], selectedTvs: TrafficVolume[]) => {
        const nbTvs = tvs?.length || 0;
        const nbTvsSelected = selectedTvs?.length || 0;
        const selectionLabelVisible = nbTvsSelected > 0;
        return (
            <Flex className="query-side-panel overflow" size="30em" direction="vertical">
                <Flex size="2em">
                    <SelectionLabel
                        visible={selectionLabelVisible}
                        title={`${nbTvsSelected} TV${nbTvsSelected > 1 ? 's' : ''} selected`}
                        subTitle={`${nbTvs} in total`}
                        onSelectAll={this.handleSelectAll(tvs)}
                        onClearSelection={this.handleClearSelection}
                        extraMenuItems={this.getSelectionLabelMenuItems}
                        style={{ marginTop: 0, marginBottom: 0 }}>
                        <div className="selection">
                            <span className="detailed-subpanel-title">TFVs</span>
                            <span className="detailed-subpanel-title-info">{nbTvs}</span>
                        </div>
                    </SelectionLabel>
                </Flex>
                <Flex size="40em">
                    <TrafficVolumeTable
                        data={tvs}
                        columns={TV_COLUMNS}
                        keyProvider={KeyUtils.TV_KEY_PROVIDER}
                        columnResizeDisabled={true}
                        noDataMessage={'No TV to be displayed'}
                        selection={this.selection()}
                        defaultSorting={[{ columnName: StandardTrafficVolumeColumn.ID.header, direction: 'asc' }]}
                    />
                </Flex>
            </Flex>
        );
    });
}

interface QueryPanelButtonsProps extends ModalPanelProps {
    onLaunchQuery?(): void;
}

class QueryPanelButtons extends PureComponent<QueryPanelButtonsProps> {
    handleCloseClick = () => {
        this.props.onClose?.();
    };
    handleQueryClick = () => {
        this.props.onLaunchQuery?.();
    };

    render() {
        return (
            <Space>
                <Button key="close" onClick={this.handleCloseClick}>
                    Close
                </Button>
                <Button key="query" onClick={this.handleQueryClick}>
                    Query
                </Button>
            </Space>
        );
    }
}

export const QueryPanel = connector(QueryPanelComponent);
